import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertTriangle,
  Bike,
  Building2,
  CalendarDays,
  Clock3,
  Database,
  Download,
  ExternalLink,
  Footprints,
  Info,
  Layers,
  ListFilter,
  LocateFixed,
  Lock,
  MapPin,
  Navigation,
  RefreshCw,
  Route,
  Search,
  ShieldAlert,
  SlidersHorizontal,
  TrafficCone,
  Waypoints,
} from 'lucide-react';
import Meta from '../components/Meta';
import {
  CivicMapBottomSheet,
  CivicMapButton,
  CivicMapDrawer,
  CivicMapPanel,
  CivicMapSurface,
  CivicSectionLabel,
} from '../components/map/CivicMapChrome';
import {
  DEFAULT_ROADWORKS_WORKS_LIMIT,
  fetchRoadworksStatus,
  fetchRoadworksWorks,
  type RoadworkItem,
  type RoadworksStatusResponse,
} from '../lib/services/roadworksApiService';
import {
  getImpactColor,
  getRoadworkCoords,
} from '../components/roadworks/roadworkMapUtils';
import {
  RECENTLY_SEEN_WINDOW_DAYS,
  compareWorks,
  formatDate,
  formatDateRange,
  formatDuration,
  formatFirstSeen,
  formatRelativeRefresh,
  getDiscoveryState,
  getRoadworkId,
  getRoadworksDataHealth,
  getSchedulePresentation,
  getWorkSubcategory,
  getWorkTiming,
  hasHighImpact,
  isActiveTravelWork,
  isEdinburghWork,
  itemDates,
  matchesTimeFilter,
  normalizeTags,
  parseAnyDate,
  searchableText,
  toText,
  type WorkSubcategory,
  type WorkTimeFilter,
} from '../lib/roadworks/cycleScopeFilters';
import { cn } from '../lib/utils';

const FILTER_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const RoadworksMap = dynamic(() => import('../components/roadworks/RoadworksMap'), {
  ssr: false,
  loading: () => (
    <div className="flex h-full w-full items-center justify-center bg-[var(--c-bg)]">
      <CivicMapPanel className="w-[min(360px,calc(100vw-32px))] border-t-2 border-t-[var(--c-text-primary)] p-5">
        <CivicSectionLabel>Map</CivicSectionLabel>
        <p className="mt-5 font-['Newsreader'] text-2xl leading-tight">Loading Edinburgh map</p>
        <p className="mt-2 text-sm leading-6 text-[var(--c-text-secondary)]">
          Preparing active travel works and map layers.
        </p>
      </CivicMapPanel>
    </div>
  ),
});

type LayerKey =
  | 'activeProgramme'
  | 'experimentalTro'
  | 'nationalCycleNetwork'
  | 'corePaths'
  | 'quietRoutes';

type LocationState =
  | { state: 'idle'; message: string }
  | { state: 'locating'; message: string }
  | { state: 'found'; message: string }
  | { state: 'error'; message: string };

const CATEGORY_ORDER: WorkSubcategory[] = [
  'cycling',
  'signals_crossings',
  'cycle_hanger',
  'shared_path',
  'pedestrian',
  'road_improvement',
];

const CATEGORY_META: Record<WorkSubcategory, { label: string; shortLabel: string; icon: LucideIcon }> = {
  cycling: { label: 'Cycle routes', shortLabel: 'Cycle', icon: Bike },
  signals_crossings: { label: 'Signals and crossings', shortLabel: 'Crossings', icon: TrafficCone },
  cycle_hanger: { label: 'Cycle parking', shortLabel: 'Parking', icon: Lock },
  shared_path: { label: 'Shared paths', shortLabel: 'Shared paths', icon: Waypoints },
  pedestrian: { label: 'Walking access', shortLabel: 'Walking', icon: Footprints },
  road_improvement: { label: 'Street and surface', shortLabel: 'Surface', icon: Route },
};

const LAYER_META: Record<LayerKey, { label: string; description: string }> = {
  activeProgramme: {
    label: 'Active Travel Programme',
    description: 'Council programme lines used to understand where street works meet planned delivery.',
  },
  experimentalTro: {
    label: 'Experimental TROs',
    description: 'Traffic regulation orders that can affect active travel streets and trial layouts.',
  },
  nationalCycleNetwork: {
    label: 'National Cycle Network',
    description: 'Reference network layer for known NCN routes.',
  },
  corePaths: {
    label: 'Adopted Core Paths',
    description: 'Edinburgh core path routes for walking and cycling context.',
  },
  quietRoutes: {
    label: 'Quiet Routes',
    description: 'Quiet route context where available.',
  },
};

function haversineMetres(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number {
  const radius = 6371000;
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * radius * Math.asin(Math.min(1, Math.sqrt(h)));
}

function formatDistance(metres: number): string {
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(metres < 10000 ? 1 : 0)} km`;
}

function findNearestWork(
  items: RoadworkItem[],
  location: { latitude: number; longitude: number }
): { item: RoadworkItem; distance: number } | null {
  let nearest: { item: RoadworkItem; distance: number } | null = null;

  for (const item of items) {
    const coords = getRoadworkCoords(item);
    if (!coords) continue;
    const distance = haversineMetres(location, { latitude: coords[1], longitude: coords[0] });
    if (!nearest || distance < nearest.distance) {
      nearest = { item, distance };
    }
  }

  return nearest;
}

function csvEscape(value: unknown): string {
  const text = toText(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function DetailFact({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[32px_1fr] gap-3 border-t border-[var(--c-border)] py-3 first:border-t-0 first:pt-0">
      <span className="flex h-8 w-8 items-center justify-center border border-[var(--c-border)] text-[var(--c-accent)]">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
      </span>
      <div>
        <p className="font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-tertiary)]">
          {label}
        </p>
        <div className="mt-1 text-sm leading-6 text-[var(--c-text-primary)]">{children}</div>
      </div>
    </div>
  );
}

function WorkSummaryRow({
  item,
  selected,
  onSelect,
}: {
  item: RoadworkItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const subcategory = getWorkSubcategory(item);
  const category = subcategory ? CATEGORY_META[subcategory] : null;
  const impact = toText(item.traffic_impact) || 'Impact not supplied';
  const impactColor = getImpactColor(impact);
  const schedule = getSchedulePresentation(item);
  const discovery = getDiscoveryState(item);
  const firstSeen = formatFirstSeen(item);
  const address = toText(item.full_address) || toText(item.location) || 'Location not supplied';
  const conciseName = [
    category?.label || toText(item.works_type) || 'Road work',
    'at ' + address,
    schedule.label,
    formatDateRange(item),
    impact + ' impact',
  ].filter(Boolean).join('. ');

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={conciseName}
      className={cn(
        'block min-h-11 w-full border border-[var(--c-border)] bg-[var(--c-bg)] p-4 text-left transition-colors hover:border-[var(--c-text-tertiary)] hover:bg-[var(--c-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]',
        selected && 'border-[var(--c-accent)] bg-[var(--c-accent-muted)]'
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className="mt-1 flex h-3 w-3 shrink-0 items-center justify-center rounded-full border-2 border-white shadow-sm"
          style={{ backgroundColor: impactColor }}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'border px-2 py-0.5 font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.12em]',
                schedule.timing === 'live'
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                  : 'border-[var(--c-border)] text-[var(--c-text-secondary)]'
              )}
            >
              {schedule.label}
            </span>
            {discovery === 'new_refresh' ? (
              <span className="border border-[var(--c-accent)] bg-[var(--c-accent-muted)] px-2 py-0.5 font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.12em] text-[var(--c-accent)]">
                New this refresh
              </span>
            ) : discovery === 'recently_seen' ? (
              <span className="border border-[var(--c-border)] px-2 py-0.5 font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.12em] text-[var(--c-text-secondary)]">
                Recently added
              </span>
            ) : null}
          </div>

          <p className="mt-2 line-clamp-2 font-['Newsreader'] text-xl leading-tight text-[var(--c-text-primary)]">
            {toText(item.description) || category?.label || 'Road works record'}
          </p>

          <p className="mt-2 flex items-start gap-1.5 text-sm leading-5 text-[var(--c-text-secondary)]">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.6} aria-hidden />
            <span className="break-words">{address}</span>
          </p>

          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-[var(--c-border)] pt-3 text-xs text-[var(--c-text-secondary)]">
            <span>
              <span className="block font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.12em] text-[var(--c-text-tertiary)]">
                Schedule
              </span>
              <span className="mt-0.5 block text-[var(--c-text-primary)]">{schedule.detail}</span>
            </span>
            <span>
              <span className="block font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.12em] text-[var(--c-text-tertiary)]">
                Impact
              </span>
              <span className="mt-0.5 block text-[var(--c-text-primary)]">{impact}</span>
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.1em] text-[var(--c-text-tertiary)]">
            <span>{category?.shortLabel || toText(item.works_type) || 'Road work'}</span>
            <span>{toText(item.reference) || 'Reference not supplied'}</span>
            {firstSeen ? <span>{firstSeen}</span> : null}
          </div>
        </div>
      </div>
    </button>
  );
}
function LayerToggle({
  label,
  description,
  checked,
  onToggle,
}: {
  label: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={checked}
      className="flex w-full items-start justify-between gap-4 border border-[var(--c-border)] bg-[var(--c-bg)] p-4 text-left hover:border-[var(--c-text-tertiary)]"
    >
      <span>
        <span className="block font-[var(--pr-grotesk)] text-sm font-semibold text-[var(--c-text-primary)]">
          {label}
        </span>
        <span className="mt-1 block text-xs leading-5 text-[var(--c-text-secondary)]">
          {description}
        </span>
      </span>
      <span
        className={cn(
          'mt-0.5 flex h-5 w-9 shrink-0 items-center border p-0.5 transition-colors',
          checked ? 'border-[var(--c-text-primary)] bg-[var(--c-text-primary)]' : 'border-[var(--c-border)] bg-[var(--c-surface)]'
        )}
        aria-hidden
      >
        <span
          className={cn(
            'h-3.5 w-3.5 bg-[var(--c-bg)] transition-transform',
            checked && 'translate-x-4'
          )}
        />
      </span>
    </button>
  );
}

function FilterOptionButton({
  label,
  description,
  count,
  active,
  muted = false,
  disabled = false,
  icon: Icon,
  onClick,
}: {
  label: string;
  description?: string;
  count?: number | string;
  active: boolean;
  muted?: boolean;
  disabled?: boolean;
  icon: LucideIcon;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'flex min-h-12 w-full items-start gap-3 border p-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)] disabled:cursor-wait disabled:opacity-55',
        active
          ? 'border-[var(--c-text-primary)] bg-[var(--c-text-primary)] text-[var(--c-bg)]'
          : 'border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-text-primary)] hover:border-[var(--c-text-tertiary)] hover:bg-[var(--c-surface-hover)]',
        muted && !active && 'opacity-75'
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.6} aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 font-[var(--pr-grotesk)] text-sm font-semibold leading-tight">
          <span>{label}</span>
          {count !== undefined ? (
            <span
              className={cn(
                'font-[var(--pr-mono)] text-[10px]',
                active ? 'opacity-70' : 'text-[var(--c-text-tertiary)]'
              )}
            >
              {count}
            </span>
          ) : null}
        </span>
        {description ? (
          <span className={cn('mt-1 block text-xs leading-5', active ? 'opacity-75' : 'text-[var(--c-text-secondary)]')}>
            {description}
          </span>
        ) : null}
      </span>
    </button>
  );
}

export default function CycleScopePage() {
  const [activeCandidates, setActiveCandidates] = useState<RoadworkItem[]>([]);
  const [contextWorks, setContextWorks] = useState<RoadworkItem[] | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const [statusPayload, setStatusPayload] = useState<RoadworksStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [timeFilter, setTimeFilter] = useState<WorkTimeFilter>('all');
  const [selectedCategories, setSelectedCategories] = useState<WorkSubcategory[]>([]);
  const [onlyHighImpact, setOnlyHighImpact] = useState(false);
  const [discoveryFilter, setDiscoveryFilter] = useState<'all' | 'new_refresh' | 'recently_seen'>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [layersOpen, setLayersOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const filtersPanelRef = useRef<HTMLDivElement | null>(null);
  const filtersPreviousFocusRef = useRef<HTMLElement | null>(null);
  const contextAbortRef = useRef<AbortController | null>(null);
  const [resultsLimit, setResultsLimit] = useState(100);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationState, setLocationState] = useState<LocationState>({
    state: 'idle',
    message: 'Use your browser location to jump to nearby works.',
  });
  const [layers, setLayers] = useState<Record<LayerKey, boolean>>({
    activeProgramme: true,
    experimentalTro: true,
    nationalCycleNetwork: false,
    corePaths: false,
    quietRoutes: false,
  });

  useEffect(() => {
    if (!filtersOpen) return undefined;
    filtersPreviousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() => {
      const preferred = filtersPanelRef.current?.querySelector<HTMLElement>('[data-dialog-close]');
      (preferred ?? filtersPanelRef.current)?.focus();
    });
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setFiltersOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleEscape);
      window.requestAnimationFrame(() => {
        const previous = filtersPreviousFocusRef.current;
        if (previous?.isConnected) {
          previous.focus();
          return;
        }
        const trigger = Array.from(
          document.querySelectorAll<HTMLElement>('[data-dialog-trigger="cycle-filters"]')
        ).find(element => element.getClientRects().length > 0);
        trigger?.focus();
      });
    };
  }, [filtersOpen]);

  const loadData = useCallback(async (signal?: AbortSignal) => {
    contextAbortRef.current?.abort();
    contextAbortRef.current = null;
    setLoading(true);
    setError(null);
    setContextError(null);
    setContextLoading(false);

    try {
      const [worksResult, statusResult] = await Promise.allSettled([
        fetchRoadworksWorks({
          limit: DEFAULT_ROADWORKS_WORKS_LIMIT,
          activeTravelOnly: true,
          signal,
        }),
        fetchRoadworksStatus({ signal }),
      ]);
      if (signal?.aborted) return;

      if (worksResult.status === 'fulfilled') {
        setActiveCandidates(worksResult.value);
        setContextWorks(null);
        setActiveOnly(true);
      } else {
        throw worksResult.reason instanceof Error
          ? worksResult.reason
          : new Error('Unable to load roadworks data');
      }

      setStatusPayload(statusResult.status === 'fulfilled' ? statusResult.value : null);
    } catch (err) {
      if (!signal?.aborted) {
        setError(err instanceof Error ? err.message : 'Unable to load CycleScope data');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadData(controller.signal);
    return () => {
      controller.abort();
      contextAbortRef.current?.abort();
    };
  }, [loadData]);

  const showRoadworksContext = useCallback(async () => {
    if (contextWorks) {
      setActiveOnly(false);
      return;
    }
    if (contextLoading) return;

    const controller = new AbortController();
    contextAbortRef.current = controller;
    setContextLoading(true);
    setContextError(null);
    try {
      const fullWorks = await fetchRoadworksWorks({
        limit: DEFAULT_ROADWORKS_WORKS_LIMIT,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setContextWorks(fullWorks);
      setActiveOnly(false);
    } catch (contextLoadError) {
      if (!controller.signal.aborted) {
        setContextError(
          contextLoadError instanceof Error
            ? contextLoadError.message
            : 'Unable to load wider roadworks context'
        );
      }
    } finally {
      if (contextAbortRef.current === controller) {
        contextAbortRef.current = null;
      }
      if (!controller.signal.aborted) setContextLoading(false);
    }
  }, [contextLoading, contextWorks]);

  const candidateEdinburghWorks = useMemo(
    () => activeCandidates.filter(isEdinburghWork),
    [activeCandidates]
  );

  const activeTravelWorks = useMemo(
    () => candidateEdinburghWorks.filter(isActiveTravelWork),
    [candidateEdinburghWorks]
  );

  const contextEdinburghWorks = useMemo(
    () => contextWorks?.filter(isEdinburghWork) ?? [],
    [contextWorks]
  );

  const scopeWorks = activeOnly ? activeTravelWorks : contextEdinburghWorks;
  const availableWorks = contextWorks ?? activeCandidates;
  const searchQuery = search.trim().toLowerCase();

  const timeCounts = useMemo(() => {
    const counts = {
      now_and_soon: 0,
      live: 0,
      next_30_days: 0,
      next_90_days: 0,
      later: 0,
    };
    for (const item of scopeWorks) {
      const timing = getWorkTiming(item);
      if (timing === 'live') counts.live += 1;
      if (timing === 'next_30_days') counts.next_30_days += 1;
      if (timing === 'next_90_days') counts.next_90_days += 1;
      if (timing === 'later') counts.later += 1;
      if (matchesTimeFilter(item, 'now_and_soon')) counts.now_and_soon += 1;
    }
    return counts;
  }, [scopeWorks]);

  const categoryCounts = useMemo(() => {
    const counts: Record<WorkSubcategory, number> = {
      cycle_hanger: 0,
      shared_path: 0,
      signals_crossings: 0,
      cycling: 0,
      pedestrian: 0,
      road_improvement: 0,
    };

    for (const item of scopeWorks) {
      if (searchQuery && !searchableText(item).includes(searchQuery)) continue;
      if (!matchesTimeFilter(item, timeFilter)) continue;
      if (onlyHighImpact && !hasHighImpact(item)) continue;
      if (discoveryFilter !== 'all' && getDiscoveryState(item) !== discoveryFilter) continue;
      const category = getWorkSubcategory(item);
      if (category) counts[category] += 1;
    }

    return counts;
  }, [discoveryFilter, onlyHighImpact, scopeWorks, searchQuery, timeFilter]);

  const filteredWorks = useMemo(() => {
    const filtered = scopeWorks.filter(item => {
      if (searchQuery && !searchableText(item).includes(searchQuery)) return false;
      if (!matchesTimeFilter(item, timeFilter)) return false;
      if (onlyHighImpact && !hasHighImpact(item)) return false;
      if (discoveryFilter !== 'all' && getDiscoveryState(item) !== discoveryFilter) return false;
      if (selectedCategories.length > 0) {
        const category = getWorkSubcategory(item);
        if (!category || !selectedCategories.includes(category)) return false;
      }
      return true;
    });

    return filtered.sort((a, b) => {
      if (discoveryFilter !== 'all') {
        const aSeen = parseAnyDate(a.first_seen_at)?.getTime() ?? 0;
        const bSeen = parseAnyDate(b.first_seen_at)?.getTime() ?? 0;
        if (aSeen !== bSeen) return bSeen - aSeen;
      }
      return compareWorks(a, b);
    });
  }, [
    discoveryFilter,
    onlyHighImpact,
    scopeWorks,
    searchQuery,
    selectedCategories,
    timeFilter,
  ]);

  useEffect(() => {
    setResultsLimit(100);
  }, [
    activeOnly,
    discoveryFilter,
    onlyHighImpact,
    searchQuery,
    selectedCategories,
    timeFilter,
  ]);

  const visibleResults = useMemo(
    () => filteredWorks.slice(0, resultsLimit),
    [filteredWorks, resultsLimit]
  );

  const selectedItem = useMemo(
    () => availableWorks.find(item => getRoadworkId(item) === selectedId) ?? null,
    [availableWorks, selectedId]
  );
  const selectedSchedule = selectedItem ? getSchedulePresentation(selectedItem) : null;
  const selectedDiscovery = selectedItem ? getDiscoveryState(selectedItem) : 'established';

  const mappedCount = useMemo(
    () => filteredWorks.filter(item => getRoadworkCoords(item) !== null).length,
    [filteredWorks]
  );

  const highImpactCount = useMemo(
    () => scopeWorks.filter(hasHighImpact).length,
    [scopeWorks]
  );

  const newRefreshCount = useMemo(
    () => scopeWorks.filter(item => getDiscoveryState(item) === 'new_refresh').length,
    [scopeWorks]
  );

  const recentlySeenCount = useMemo(
    () => scopeWorks.filter(item => getDiscoveryState(item) === 'recently_seen').length,
    [scopeWorks]
  );

  const dataHealth = useMemo(
    () => getRoadworksDataHealth(statusPayload),
    [statusPayload]
  );
  const outsideBoundaryCount = Math.max(
    0,
    activeCandidates.length - candidateEdinburghWorks.length
  );
  const excludedIrrelevantCount = Math.max(
    0,
    candidateEdinburghWorks.length - activeTravelWorks.length
  );
  const sourceTotalCount =
    statusPayload?.works_count ??
    activeCandidates.length;
  const sourceActiveCandidateCount =
    statusPayload?.active_travel_count ??
    activeCandidates.length;

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (!activeOnly) count += 1;
    if (timeFilter !== 'all') count += 1;
    if (onlyHighImpact) count += 1;
    if (discoveryFilter !== 'all') count += 1;
    count += selectedCategories.length;
    return count;
  }, [
    activeOnly,
    discoveryFilter,
    onlyHighImpact,
    selectedCategories.length,
    timeFilter,
  ]);
  const selectItem = useCallback((item: RoadworkItem | null) => {
    setSelectedId(item ? getRoadworkId(item) : null);
    if (item) {
      setFiltersOpen(false);
      setLayersOpen(false);
      setResultsOpen(false);
      setHealthOpen(false);
    }
  }, []);

  const toggleFilters = useCallback(() => {
    setSelectedId(null);
    setFiltersOpen(open => !open);
    setLayersOpen(false);
    setResultsOpen(false);
    setHealthOpen(false);
  }, []);

  const openLayers = useCallback(() => {
    setSelectedId(null);
    setFiltersOpen(false);
    setLayersOpen(true);
    setResultsOpen(false);
    setHealthOpen(false);
  }, []);

  const openResults = useCallback(() => {
    if (typeof document !== 'undefined') {
      const trigger = Array.from(
        document.querySelectorAll<HTMLElement>('[data-dialog-trigger="cycle-results"]')
      ).find(element => element.getClientRects().length > 0);
      trigger?.focus();
    }
    setSelectedId(null);
    setFiltersOpen(false);
    setLayersOpen(false);
    setResultsOpen(true);
    setHealthOpen(false);
  }, []);

  const openHealth = useCallback(() => {
    setSelectedId(null);
    setFiltersOpen(false);
    setLayersOpen(false);
    setResultsOpen(false);
    setHealthOpen(true);
  }, []);

  const toggleCategory = useCallback((category: WorkSubcategory) => {
    setSelectedCategories(current =>
      current.includes(category)
        ? current.filter(item => item !== category)
        : [...current, category]
    );
  }, []);

  const clearFilterOptions = useCallback(() => {
    setActiveOnly(true);
    setTimeFilter('all');
    setSelectedCategories([]);
    setOnlyHighImpact(false);
    setDiscoveryFilter('all');
  }, []);

  const resetFilters = useCallback(() => {
    setSearch('');
    setActiveOnly(true);
    setTimeFilter('all');
    setSelectedCategories([]);
    setOnlyHighImpact(false);
    setDiscoveryFilter('all');
    setSelectedId(null);
  }, []);

  const handleNearMe = useCallback(() => {
    if (typeof window === 'undefined' || !navigator.geolocation) {
      setLocationState({
        state: 'error',
        message: 'Location is not available in this browser.',
      });
      return;
    }

    setLocationState({ state: 'locating', message: 'Finding your location...' });

    navigator.geolocation.getCurrentPosition(
      position => {
        const nextLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setUserLocation(nextLocation);

        const nearest = findNearestWork(filteredWorks.length > 0 ? filteredWorks : activeTravelWorks, nextLocation);
        if (nearest) {
          selectItem(nearest.item);
          setLocationState({
            state: 'found',
            message: `Nearest matching work is ${formatDistance(nearest.distance)} away.`,
          });
        } else {
          setLocationState({
            state: 'found',
            message: 'Location set. No mapped active-travel works match the current filters.',
          });
        }
      },
      () => {
        setLocationState({
          state: 'error',
          message: 'Location permission was not granted.',
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [activeTravelWorks, filteredWorks, selectItem]);

  const exportCsv = useCallback(() => {
    if (typeof window === 'undefined' || filteredWorks.length === 0) return;
    const headers = [
      'reference',
      'description',
      'location',
      'full_address',
      'organisation',
      'status',
      'schedule_state',
      'traffic_impact',
      'category',
      'works_type',
      'start',
      'end',
      'duration',
      'new_this_refresh',
      'first_seen_at',
      'source',
      'tags',
    ];
    const rows = filteredWorks.map(item => [
      item.reference,
      item.description,
      item.location,
      item.full_address,
      item.promoter_name,
      item.status,
      getWorkTiming(item),
      item.traffic_impact,
      getWorkSubcategory(item),
      item.works_type,
      itemDates(item).start ? formatDate(itemDates(item).start) : '',
      itemDates(item).end ? formatDate(itemDates(item).end) : '',
      formatDuration(item),
      item.is_new ? 'true' : 'false',
      item.first_seen_at,
      item.url,
      normalizeTags(item.tags).join('|'),
    ].map(csvEscape).join(','));

    const blob = new Blob([[headers.join(','), ...rows].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cyclescope-edinburgh-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, [filteredWorks]);

  const shownCountLabel = filteredWorks.length.toLocaleString();
  const relevantCountLabel = activeTravelWorks.length.toLocaleString();
  const contextCountLabel = contextLoading
    ? 'Loading…'
    : contextWorks
      ? contextEdinburghWorks.length.toLocaleString()
      : contextError
        ? 'Retry'
        : 'Load';
  const liveCountLabel = timeCounts.live.toLocaleString();
  const newRefreshCountLabel = newRefreshCount.toLocaleString();

  return (
    <>
      <Meta
        title="CycleScope Edinburgh Active Travel Works Map | City Scope"
        description="CycleScope is City Scope's mobile-first map of Edinburgh active-travel works, cycling projects, crossings, shared paths and street works from live roadworks data."
        canonical="/cycle-scope"
        keywords="CycleScope, City Scope CycleScope, Edinburgh active travel map, Edinburgh cycle works, Edinburgh cycling roadworks, active travel Edinburgh, City-Scope"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'CycleScope Edinburgh Active Travel Works Map',
          applicationCategory: 'MapApplication',
          operatingSystem: 'Web',
          description: 'A searchable active-travel works map for Edinburgh by City Scope.',
          url: 'https://city-scope.co.uk/cycle-scope',
        }}
      />

      <div className="planning-register flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-[var(--c-bg)] text-[var(--c-text-primary)]">
        <div className="h-[var(--navbar-height,64px)] shrink-0" />
        <main className="relative min-h-0 flex-1 overflow-hidden">
          <CivicMapSurface>
            <div className="absolute inset-0 z-0">
              <RoadworksMap
                works={scopeWorks}
                filteredWorks={filteredWorks}
                selectedId={selectedId}
                onSelect={selectItem}
                onMapReady={() => setMapReady(true)}
                userLocation={userLocation}
                showActiveTravelProgramme={layers.activeProgramme}
                showExperimentalTRO={layers.experimentalTro}
                showNationalCycleNetwork={layers.nationalCycleNetwork}
                showAdoptedCorePaths={layers.corePaths}
                showQuietRoutes={layers.quietRoutes}
              />
            </div>

            <div className="pointer-events-none absolute inset-x-0 top-0 z-[1300] p-2 sm:p-3 lg:p-5">
              <CivicMapPanel className="pointer-events-auto mx-auto w-full max-w-6xl border-t-2 border-t-[var(--c-text-primary)] p-2.5 sm:p-4">
                <div className="flex flex-col gap-2.5 lg:flex-row lg:items-end lg:justify-between">
                  <div className="min-w-0">
                    <p className="font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.18em] text-[var(--c-accent)] sm:text-[10px] sm:tracking-[0.22em]">
                      CityScope / CycleScope
                    </p>
                    <h1 className="mt-0.5 font-['Newsreader'] text-xl leading-none text-[var(--c-text-primary)] sm:mt-1 sm:text-4xl lg:text-5xl">
                      Edinburgh cycling and walking works
                    </h1>
                    <p className="mt-2 hidden max-w-2xl text-sm leading-6 text-[var(--c-text-secondary)] sm:block">
                      Map-first tracking for cycling, walking, crossings, shared paths and council street works.
                    </p>
                  </div>
                  <div className="hidden grid-cols-3 border border-[var(--c-border)] bg-[var(--c-bg)] text-left sm:grid lg:w-[360px]">
                    <div className="border-r border-[var(--c-border)] p-3">
                      <p className="font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.16em] text-[var(--c-text-tertiary)]">
                        Live now
                      </p>
                      <p className="mt-1 font-['Newsreader'] text-2xl leading-none">{liveCountLabel}</p>
                    </div>
                    <div className="border-r border-[var(--c-border)] p-3">
                      <p className="font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.16em] text-[var(--c-text-tertiary)]">
                        New this refresh
                      </p>
                      <p className="mt-1 font-['Newsreader'] text-2xl leading-none">{newRefreshCountLabel}</p>
                    </div>
                    <div className="p-3">
                      <p className="font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.16em] text-[var(--c-text-tertiary)]">
                        Shown
                      </p>
                      <p className="mt-1 font-['Newsreader'] text-2xl leading-none">{shownCountLabel}</p>
                    </div>
                  </div>
                </div>

                <div className="relative mt-2 border-t border-[var(--c-border)] pt-2.5 sm:mt-4 sm:pt-4">
                  <div className="flex gap-2 sm:flex-col lg:flex-row">
                    <label className="relative min-w-0 flex-1">
                      <span className="sr-only">Search CycleScope data</span>
                      <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--c-accent)]" strokeWidth={1.8} />
                      <input
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder="Street or ref…"
                        className="h-11 w-full border border-[var(--c-border)] bg-[var(--c-bg)] pl-11 pr-4 text-base text-[var(--c-text-primary)] placeholder:text-[var(--c-text-tertiary)] focus:border-[var(--c-accent)] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)] sm:h-12"
                      />
                    </label>
                    <div className="flex shrink-0 gap-2 sm:hidden">
                      <CivicMapButton
                        label={activeFilterCount ? `Filters, ${activeFilterCount} active` : 'Filters'}
                        icon={SlidersHorizontal}
                        onClick={toggleFilters}
                        dialogTrigger="cycle-filters"
                        active={filtersOpen || activeFilterCount > 0}
                        badge={activeFilterCount}
                        compact
                      />
                      <CivicMapButton
                        label={locationState.state === 'locating' ? 'Locating' : 'Near me'}
                        icon={LocateFixed}
                        onClick={handleNearMe}
                        active={locationState.state === 'found'}
                        disabled={locationState.state === 'locating'}
                        compact
                      />
                      <CivicMapButton
                        label="Layers"
                        icon={Layers}
                        onClick={openLayers}
                        active={layersOpen}
                        compact
                      />
                      <CivicMapButton
                        label="Results"
                        icon={ListFilter}
                        onClick={openResults}
                        dialogTrigger="cycle-results"
                        active={resultsOpen}
                        badge={filteredWorks.length > 99 ? '99+' : filteredWorks.length}
                        compact
                      />
                    </div>
                    <div className="hidden gap-2 sm:flex">
                      <CivicMapButton
                        label={activeFilterCount ? `Filters ${activeFilterCount}` : 'Filters'}
                        icon={SlidersHorizontal}
                        onClick={toggleFilters}
                        dialogTrigger="cycle-filters"
                        active={filtersOpen || activeFilterCount > 0}
                        className="flex-1 lg:flex-none"
                      />
                      <CivicMapButton
                        label={locationState.state === 'locating' ? 'Locating' : 'Near me'}
                        icon={LocateFixed}
                        onClick={handleNearMe}
                        active={locationState.state === 'found'}
                        disabled={locationState.state === 'locating'}
                        className="flex-1 lg:flex-none"
                      />
                      <CivicMapButton
                        label="Layers"
                        icon={Layers}
                        onClick={openLayers}
                        active={layersOpen}
                        className="flex-1 lg:flex-none"
                      />
                      <CivicMapButton
                        label={'Results ' + shownCountLabel}
                        icon={ListFilter}
                        onClick={openResults}
                        dialogTrigger="cycle-results"
                        active={resultsOpen}
                        className="flex-1 lg:flex-none"
                      />
                    </div>
                  </div>

                  {filtersOpen && typeof document !== 'undefined' ? createPortal(
                    <>
                      <button
                        type="button"
                        aria-label="Close filters"
                        onClick={() => setFiltersOpen(false)}
                        className="fixed inset-0 z-[3600] cursor-default bg-black/10"
                      />
                      <div
                      ref={filtersPanelRef}
                      role="dialog"
                      aria-modal="true"
                      aria-label="CycleScope filters"
                      tabIndex={-1}
                      onKeyDown={event => {
                        if (event.key !== 'Tab' || !filtersPanelRef.current) return;
                        const focusable = Array.from(
                          filtersPanelRef.current.querySelectorAll<HTMLElement>(FILTER_FOCUSABLE_SELECTOR)
                        ).filter(element => !element.hasAttribute('disabled'));
                        if (focusable.length === 0) {
                          event.preventDefault();
                          filtersPanelRef.current.focus();
                          return;
                        }
                        const first = focusable[0];
                        const last = focusable[focusable.length - 1];
                        if (event.shiftKey && document.activeElement === first) {
                          event.preventDefault();
                          last.focus();
                        } else if (!event.shiftKey && document.activeElement === last) {
                          event.preventDefault();
                          first.focus();
                        }
                      }}
                      className="fixed left-3 right-3 top-[88px] z-[3700] max-h-[calc(100dvh-104px)] overflow-y-auto border border-[var(--c-border)] border-t-2 border-t-[var(--c-text-primary)] bg-[var(--c-surface)] p-3 shadow-lg sm:left-auto sm:right-4 sm:top-20 sm:max-h-[calc(100dvh-96px)] sm:w-[560px] sm:p-4"
                    >
                      <div className="sticky -top-3 z-10 -mx-3 -mt-3 flex items-center gap-3 border-b border-[var(--c-border)] bg-[var(--c-surface)] p-3 sm:-top-4 sm:-mx-4 sm:-mt-4 sm:p-4">
                        <CivicSectionLabel className="min-w-0 flex-1">Filters</CivicSectionLabel>
                        <div className="flex shrink-0 items-center gap-2">
                          {activeFilterCount > 0 ? (
                            <button
                              type="button"
                              onClick={clearFilterOptions}
                              className="min-h-11 border border-[var(--c-border)] px-2.5 py-2 font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.12em] text-[var(--c-text-secondary)] hover:border-[var(--c-text-primary)] hover:text-[var(--c-text-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]"
                            >
                              Clear
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => setFiltersOpen(false)}
                            data-dialog-close
                            className="min-h-11 border border-[var(--c-text-primary)] bg-[var(--c-text-primary)] px-2.5 py-2 font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.12em] text-[var(--c-bg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]"
                          >
                            {'Show ' + shownCountLabel}
                          </button>
                        </div>
                      </div>

                      <p className="mt-3 text-sm leading-6 text-[var(--c-text-secondary)]">
                        {activeFilterCount > 0
                          ? `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} active.`
                          : 'No optional filters. Active-travel scope remains selected.'}
                      </p>

                      <div className="mt-4">
                        <CivicSectionLabel>Scope</CivicSectionLabel>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <FilterOptionButton
                            label="Relevant active travel"
                            description="Cycling, walking, crossings and surface works inside the council boundary."
                            count={relevantCountLabel}
                            active={activeOnly}
                            icon={Bike}
                            onClick={() => setActiveOnly(true)}
                          />
                          <FilterOptionButton
                            label="All roadworks context"
                            description={contextLoading
                              ? 'Loading the wider Edinburgh roadworks feed…'
                              : 'Loaded only when requested, then constrained to the council boundary.'}
                            count={contextCountLabel}
                            active={!activeOnly}
                            muted
                            icon={SlidersHorizontal}
                            onClick={() => { void showRoadworksContext(); }}
                          />
                        </div>
                        {contextError ? (
                          <p
                            className="mt-2 border border-amber-300 bg-amber-50 p-2 text-xs leading-5 text-amber-950"
                            role="alert"
                          >
                            Wider roadworks context could not load. The complete active-travel view is still available; select context again to retry.
                          </p>
                        ) : null}
                      </div>

                      <div className="mt-4 border-t border-[var(--c-border)] pt-4">
                        <CivicSectionLabel>When</CivicSectionLabel>
                        <p className="mt-2 text-xs leading-5 text-[var(--c-text-secondary)]">
                          All relevant dates are shown by default so long-term schemes are not hidden. Narrow the view when you only need current or near-term work.
                        </p>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <FilterOptionButton
                            label="Now + next 14 days"
                            count={timeCounts.now_and_soon}
                            active={timeFilter === 'now_and_soon'}
                            icon={Clock3}
                            onClick={() => setTimeFilter('now_and_soon')}
                          />
                          <FilterOptionButton
                            label="Happening now"
                            count={timeCounts.live}
                            active={timeFilter === 'live'}
                            icon={Activity}
                            onClick={() => setTimeFilter('live')}
                          />
                          <FilterOptionButton
                            label="Starts within 30 days"
                            count={timeCounts.next_30_days}
                            active={timeFilter === 'next_30_days'}
                            icon={CalendarDays}
                            onClick={() => setTimeFilter('next_30_days')}
                          />
                          <FilterOptionButton
                            label="Starts in 31–90 days"
                            count={timeCounts.next_90_days}
                            active={timeFilter === 'next_90_days'}
                            icon={CalendarDays}
                            onClick={() => setTimeFilter('next_90_days')}
                          />
                          <FilterOptionButton
                            label="Long-term planned"
                            count={timeCounts.later}
                            active={timeFilter === 'later'}
                            icon={Route}
                            onClick={() => setTimeFilter('later')}
                          />
                          <FilterOptionButton
                            label="All dates"
                            count={scopeWorks.length}
                            active={timeFilter === 'all'}
                            icon={ListFilter}
                            onClick={() => setTimeFilter('all')}
                          />
                        </div>
                      </div>

                      <div className="mt-4 border-t border-[var(--c-border)] pt-4">
                        <CivicSectionLabel>What changed</CivicSectionLabel>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <FilterOptionButton
                            label="New this refresh"
                            description="Added by the latest collector refresh."
                            count={newRefreshCount}
                            active={discoveryFilter === 'new_refresh'}
                            icon={RefreshCw}
                            onClick={() => setDiscoveryFilter(
                              discoveryFilter === 'new_refresh' ? 'all' : 'new_refresh'
                            )}
                          />
                          <FilterOptionButton
                            label="Recently discovered"
                            description={'First seen in the last ' + RECENTLY_SEEN_WINDOW_DAYS + ' days, excluding this refresh.'}
                            count={recentlySeenCount}
                            active={discoveryFilter === 'recently_seen'}
                            icon={Clock3}
                            onClick={() => setDiscoveryFilter(
                              discoveryFilter === 'recently_seen' ? 'all' : 'recently_seen'
                            )}
                          />
                          <FilterOptionButton
                            label="High impact"
                            description="Road closures and the highest source impact rating."
                            count={highImpactCount}
                            active={onlyHighImpact}
                            icon={AlertTriangle}
                            onClick={() => setOnlyHighImpact(value => !value)}
                          />
                        </div>
                      </div>
                      <div className="mt-4 border-t border-[var(--c-border)] pt-4">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <p className="font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-tertiary)]">
                            Categories
                          </p>
                          {selectedCategories.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => setSelectedCategories([])}
                              className="font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.12em] text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]"
                            >
                              All categories
                            </button>
                          ) : null}
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                          <FilterOptionButton
                            label="All categories"
                            description="Do not restrict by active-travel type."
                            active={selectedCategories.length === 0}
                            icon={ListFilter}
                            onClick={() => setSelectedCategories([])}
                          />
                          {CATEGORY_ORDER.map(category => {
                            const meta = CATEGORY_META[category];
                            return (
                              <FilterOptionButton
                                key={category}
                                label={meta.label}
                                count={categoryCounts[category]}
                                active={selectedCategories.includes(category)}
                                icon={meta.icon}
                                onClick={() => toggleCategory(category)}
                              />
                            );
                          })}
                        </div>
                      </div>
                      </div>
                    </>,
                    document.body
                  ) : null}

                  <div className="mt-2 flex items-center justify-between gap-2 border-t border-[var(--c-border)] pt-2">
                    <button
                      type="button"
                      onClick={openHealth}
                      className="flex min-h-11 min-w-0 items-center gap-2 text-left text-xs text-[var(--c-text-secondary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]"
                      aria-label={dataHealth.label + '. ' + dataHealth.summary + ' Open data details.'}
                    >
                      <span
                        className={cn(
                          'h-2.5 w-2.5 shrink-0 rounded-full',
                          dataHealth.level === 'healthy'
                            ? 'bg-emerald-600'
                            : dataHealth.level === 'warning'
                              ? 'bg-amber-500'
                              : 'bg-slate-400'
                        )}
                        aria-hidden
                      />
                      <span className="font-semibold text-[var(--c-text-primary)]">{dataHealth.label}</span>
                      <span className="hidden truncate sm:inline">{dataHealth.summary}</span>
                      <Info className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} aria-hidden />
                    </button>
                    <span className="shrink-0 font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.1em] text-[var(--c-text-tertiary)]">
                      {dataHealth.updatedAt
                        ? formatRelativeRefresh(dataHealth.updatedAt)
                        : mapReady
                          ? 'Map ready'
                          : 'Map loading'}
                    </span>
                  </div>

                  {locationState.state !== 'idle' ? (
                    <p className="mt-1 text-xs text-[var(--c-text-secondary)]" role="status">
                      {locationState.message}
                    </p>
                  ) : null}
                </div>
              </CivicMapPanel>
            </div>

            {loading ? (
              <div className="pointer-events-none absolute inset-x-3 bottom-4 z-[1200] flex justify-center md:bottom-6">
                <CivicMapPanel className="pointer-events-auto w-full max-w-md border-t-2 border-t-[var(--c-text-primary)] p-4">
                  <CivicSectionLabel>Loading</CivicSectionLabel>
                  <p className="mt-3 font-['Newsreader'] text-2xl">Fetching active travel works</p>
                  <p className="mt-1 text-sm text-[var(--c-text-secondary)]">
                    The map remains available while the latest records load.
                  </p>
                </CivicMapPanel>
              </div>
            ) : null}

            {!loading && error ? (
              <div className="absolute inset-x-3 bottom-4 z-[1200] flex justify-center md:bottom-6">
                <CivicMapPanel className="w-full max-w-md border-t-2 border-t-[var(--c-error)] p-4">
                  <CivicSectionLabel>Data issue</CivicSectionLabel>
                  <p className="mt-3 font-['Newsreader'] text-2xl">CycleScope could not load</p>
                  <p className="mt-1 text-sm text-[var(--c-text-secondary)]">{error}</p>
                  <button
                    type="button"
                    onClick={() => { void loadData(); }}
                    className="mt-4 min-h-11 border border-[var(--c-border)] bg-[var(--c-text-primary)] px-4 py-2 text-sm font-semibold text-[var(--c-bg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]"
                  >
                    Try again
                  </button>
                </CivicMapPanel>
              </div>
            ) : null}

            {!loading && !error && filteredWorks.length === 0 ? (
              <div className="absolute inset-x-3 bottom-4 z-[1200] flex justify-center md:bottom-6">
                <CivicMapPanel className="w-full max-w-md border-t-2 border-t-[var(--c-text-primary)] p-4">
                  <CivicSectionLabel>No matches</CivicSectionLabel>
                  <p className="mt-3 font-['Newsreader'] text-2xl">No works match this timeframe</p>
                  <p className="mt-1 text-sm leading-6 text-[var(--c-text-secondary)]">
                    Nothing is hidden as “success”. Try all dates, remove the street search, or include wider roadworks context.
                  </p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-4 min-h-11 border border-[var(--c-border)] bg-[var(--c-text-primary)] px-4 py-2 text-sm font-semibold text-[var(--c-bg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]"
                  >
                    Reset view
                  </button>
                </CivicMapPanel>
              </div>
            ) : null}

            <CivicMapBottomSheet
              open={Boolean(selectedItem)}
              title={toText(selectedItem?.full_address) || toText(selectedItem?.location) || 'Selected work'}
              eyebrow={toText(selectedItem?.reference) || 'CycleScope'}
              onClose={() => setSelectedId(null)}
              footer={
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={openResults}
                    className="min-h-11 px-2 text-sm font-semibold text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]"
                  >
                    View list
                  </button>
                  {selectedItem?.url ? (
                    <a
                      href={selectedItem.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 border border-[var(--c-border)] bg-[var(--c-text-primary)] px-3 py-2 text-sm font-semibold text-[var(--c-bg)]"
                    >
                      Source
                      <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.6} />
                    </a>
                  ) : null}
                </div>
              }
            >
              {selectedItem ? (
                <div>
                  <div className="mb-4 flex flex-wrap gap-2">
                    {getWorkSubcategory(selectedItem) ? (
                      <span className="border border-[var(--c-border)] bg-[var(--c-bg)] px-2.5 py-1 font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.12em] text-[var(--c-accent)]">
                        {CATEGORY_META[getWorkSubcategory(selectedItem) as WorkSubcategory].label}
                      </span>
                    ) : null}
                    <span
                      className="border px-2.5 py-1 font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.12em]"
                      style={{
                        borderColor: getImpactColor(toText(selectedItem.traffic_impact)),
                        color: getImpactColor(toText(selectedItem.traffic_impact)),
                      }}
                    >
                      {toText(selectedItem.traffic_impact) || 'Impact not listed'}
                    </span>
                    {selectedDiscovery === 'new_refresh' ? (
                      <span className="border border-[var(--c-accent)] bg-[var(--c-accent-muted)] px-2.5 py-1 font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.12em] text-[var(--c-accent)]">
                        New this refresh
                      </span>
                    ) : selectedDiscovery === 'recently_seen' ? (
                      <span className="border border-[var(--c-border)] px-2.5 py-1 font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.12em] text-[var(--c-text-secondary)]">
                        Recently discovered
                      </span>
                    ) : null}
                  </div>

                  <DetailFact icon={Clock3} label="Timing">
                    <span>{selectedSchedule?.label ?? 'Schedule unconfirmed'}</span>
                    <span className="mt-1 block text-xs text-[var(--c-text-tertiary)]">{selectedSchedule?.detail}</span>
                  </DetailFact>
                  <DetailFact icon={MapPin} label="Location">
                    {toText(selectedItem.full_address) || toText(selectedItem.location) || 'Location not listed'}
                  </DetailFact>
                  <DetailFact icon={CalendarDays} label="Dates">
                    <span>{formatDateRange(selectedItem)}</span>
                    <span className="mt-1 block text-xs text-[var(--c-text-tertiary)]">
                      {formatDuration(selectedItem)} calendar span
                    </span>
                  </DetailFact>
                  {formatFirstSeen(selectedItem) ? (
                    <DetailFact icon={RefreshCw} label="Added to CycleScope">
                      <span>{formatFirstSeen(selectedItem)}</span>
                      <span className="mt-1 block text-xs text-[var(--c-text-tertiary)]">
                        {selectedDiscovery === 'new_refresh'
                          ? 'Included by the latest collector refresh.'
                          : 'This is separate from the work start date.'}
                      </span>
                    </DetailFact>
                  ) : null}
                  <DetailFact icon={Route} label="Description">
                    {toText(selectedItem.description) || 'No description available.'}
                  </DetailFact>
                  <DetailFact icon={Building2} label="Organisation">
                    {toText(selectedItem.promoter_name) || 'Organisation not listed'}
                  </DetailFact>
                  <DetailFact icon={Info} label="Source status">
                    {[toText(selectedItem.status), toText(selectedItem.works_type)]
                      .filter(Boolean)
                      .join(' · ') || 'Status not supplied'}
                  </DetailFact>
                  <DetailFact icon={Navigation} label="Source">
                    {selectedItem.url ? (
                      <a
                        href={selectedItem.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-[var(--c-accent)] hover:text-[var(--c-accent-hover)]"
                      >
                        Scottish Road Works Online
                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.6} />
                      </a>
                    ) : (
                      'Source link not listed'
                    )}
                  </DetailFact>
                </div>
              ) : null}
            </CivicMapBottomSheet>

            <CivicMapDrawer
              open={healthOpen}
              title="Data coverage"
              eyebrow={dataHealth.label}
              onClose={() => setHealthOpen(false)}
              footer={
                <button
                  type="button"
                  onClick={() => { void loadData(); }}
                  disabled={loading}
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 border border-[var(--c-text-primary)] bg-[var(--c-text-primary)] px-4 py-2 text-sm font-semibold text-[var(--c-bg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)] disabled:opacity-50"
                >
                  <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} strokeWidth={1.7} aria-hidden />
                  {loading ? 'Refreshing data' : 'Refresh now'}
                </button>
              }
            >
              <div
                className={cn(
                  'border p-4',
                  dataHealth.level === 'warning'
                    ? 'border-amber-400 bg-amber-50 text-amber-950'
                    : 'border-[var(--c-border)] bg-[var(--c-bg)]'
                )}
              >
                <div className="flex items-start gap-3">
                  {dataHealth.level === 'warning' ? (
                    <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.6} aria-hidden />
                  ) : (
                    <Database className="mt-0.5 h-5 w-5 shrink-0 text-[var(--c-accent)]" strokeWidth={1.6} aria-hidden />
                  )}
                  <div>
                    <p className="font-semibold">{dataHealth.label}</p>
                    <p className="mt-1 text-sm leading-6">{dataHealth.summary}</p>
                  </div>
                </div>
              </div>

              <dl className="mt-4 grid grid-cols-2 border border-[var(--c-border)] bg-[var(--c-bg)]">
                {[
                  ['Source records', sourceTotalCount.toLocaleString()],
                  ['Active candidates', sourceActiveCandidateCount.toLocaleString()],
                  ['Inside boundary', candidateEdinburghWorks.length.toLocaleString()],
                  ['Relevant works', activeTravelWorks.length.toLocaleString()],
                  ['Mapped in view', mappedCount.toLocaleString()],
                  ['New this refresh', newRefreshCount.toLocaleString()],
                ].map(([label, value], index) => (
                  <div
                    key={label}
                    className={cn(
                      'p-3',
                      index % 2 === 0 && 'border-r border-[var(--c-border)]',
                      index < 4 && 'border-b border-[var(--c-border)]'
                    )}
                  >
                    <dt className="font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.12em] text-[var(--c-text-tertiary)]">
                      {label}
                    </dt>
                    <dd className="mt-1 font-['Newsreader'] text-2xl">{value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-4">
                <CivicSectionLabel>Refresh checks</CivicSectionLabel>
                <ul className="mt-3 space-y-2 text-sm leading-6 text-[var(--c-text-secondary)]">
                  {dataHealth.details.map(detail => (
                    <li key={detail} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--c-accent)]" aria-hidden />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 border border-[var(--c-border)] bg-[var(--c-bg)] p-4">
                <p className="font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-tertiary)]">
                  What CycleScope includes
                </p>
                <p className="mt-2 text-sm leading-6 text-[var(--c-text-secondary)]">
                  Records are checked against the City of Edinburgh council boundary. The focused view keeps explicit cycling, walking, crossing and relevant council surface work, while excluding utility excavations and event closures.
                </p>
                <p className="mt-2 text-xs leading-5 text-[var(--c-text-tertiary)]">
                  Of the active-travel candidate feed, {outsideBoundaryCount.toLocaleString()} out-of-boundary and {excludedIrrelevantCount.toLocaleString()} non-relevant records are currently excluded from the focused view.
                </p>
              </div>
            </CivicMapDrawer>

            <CivicMapDrawer
              open={layersOpen}
              title="Layers"
              eyebrow="Map context"
              onClose={() => setLayersOpen(false)}
            >
              <div className="mb-5">
                <CivicSectionLabel>Legend</CivicSectionLabel>
                <div className="mt-3 border border-[var(--c-border)] bg-[var(--c-bg)] p-3">
                  <p className="font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
                    Work markers / source impact
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-[var(--c-text-secondary)]">
                    {[
                      ['#FF4D4F', 'Road closure'],
                      ['#FF8C00', 'High'],
                      ['#D3AF37', 'Medium'],
                      ['#4ADE80', 'Low'],
                    ].map(([color, label]) => (
                      <span key={label} className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full border border-white shadow-sm" style={{ backgroundColor: color }} aria-hidden />
                        {label}
                      </span>
                    ))}
                  </div>
                  <p className="mt-4 font-[var(--pr-mono)] text-[9px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
                    Route lines
                  </p>
                  <div className="mt-2 space-y-2 text-xs text-[var(--c-text-secondary)]">
                    {[
                      ['#001296', 'solid', 'Active Travel Programme'],
                      ['#00C5FF', 'solid', 'Experimental traffic orders'],
                      ['#60A5FA', 'solid', 'National Cycle Network'],
                      ['#10B981', 'solid', 'Adopted Core Paths'],
                      ['#C084FC', 'dashed', 'Quiet Routes'],
                    ].map(([color, style, label]) => (
                      <span key={label} className="flex items-center gap-2">
                        <span
                          className={cn('h-0 w-8 border-t-2', style === 'dashed' && 'border-dashed')}
                          style={{ borderColor: color }}
                          aria-hidden
                        />
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                {(Object.keys(LAYER_META) as LayerKey[]).map(key => (
                  <LayerToggle
                    key={key}
                    label={LAYER_META[key].label}
                    description={LAYER_META[key].description}
                    checked={layers[key]}
                    onToggle={() => setLayers(current => ({ ...current, [key]: !current[key] }))}
                  />
                ))}
              </div>
              <div className="mt-5 border border-[var(--c-border)] bg-[var(--c-bg)] p-4">
                <p className="font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-tertiary)]">
                  Roadworks context
                </p>
                <p className="mt-2 text-sm leading-6 text-[var(--c-text-secondary)]">
                  Generic roadworks are available from the chip bar, but CycleScope defaults to active-travel works so cycling and walking changes stay prominent.
                </p>
              </div>
            </CivicMapDrawer>

            <CivicMapDrawer
              open={resultsOpen}
              title="Works"
              eyebrow={`${shownCountLabel} shown`}
              onClose={() => setResultsOpen(false)}
              footer={
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="text-sm font-semibold text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={exportCsv}
                    disabled={filteredWorks.length === 0}
                    className="inline-flex min-h-11 items-center gap-2 border border-[var(--c-border)] bg-[var(--c-text-primary)] px-3 py-2 text-sm font-semibold text-[var(--c-bg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)] disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" strokeWidth={1.6} />
                    Export
                  </button>
                </div>
              }
            >
              <div className="space-y-3">
                <div className="border border-[var(--c-border)] bg-[var(--c-bg)] p-3 text-xs leading-5 text-[var(--c-text-secondary)]">
                  Showing {visibleResults.length.toLocaleString()} of {filteredWorks.length.toLocaleString()} matches.
                  Live work is listed first, then the soonest upcoming start.
                </div>
                {visibleResults.map(item => {
                  const id = getRoadworkId(item);
                  return (
                    <WorkSummaryRow
                      key={id}
                      item={item}
                      selected={id === selectedId}
                      onSelect={() => selectItem(item)}
                    />
                  );
                })}
                {visibleResults.length < filteredWorks.length ? (
                  <button
                    type="button"
                    onClick={() => setResultsLimit(limit => limit + 100)}
                    className="min-h-11 w-full border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-3 text-sm font-semibold text-[var(--c-text-primary)] hover:border-[var(--c-text-tertiary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]"
                  >
                    {'Show next ' + Math.min(100, filteredWorks.length - visibleResults.length).toLocaleString()}
                  </button>
                ) : null}
              </div>
            </CivicMapDrawer>
            <p className="sr-only" role="status" aria-live="polite">
              {filteredWorks.length.toLocaleString()} CycleScope results match the current filters.
            </p>
          </CivicMapSurface>
        </main>
      </div>
    </>
  );
}
